import { useSuspenseQuery } from "@tanstack/react-query";

import { MyFlight } from "@/features/bookings/components/MyFlight";
import { MyHotel } from "@/features/bookings/components/MyHotel";
import { OrderDetailsWithoutReservation } from "@/features/bookings/components/OrderDetailsWithoutReservation";
import { reservationQueryOptions } from "@/features/bookings/hooks/useReservations";
import { ReservationType } from "@/features/bookings/types/types";
import { MyTrip } from "@/features/trips/components/bookingDetails/MyTrip";
import { tripBookingQueryOptions } from "@/features/trips/hooks/useTripBookings";
import type { SearchScope } from "@/types/types";

interface Props {
  reservationId: string;
  scope: SearchScope;
}

export const MyBooking = ({ scope, reservationId }: Props) => {
  if (scope === "hotels") {
    return <MyHotelReservation reservationId={reservationId} />;
  }

  if (scope === "flights") {
    return <MyFlightOrder reservationId={reservationId} />;
  }

  if (scope === "trips") {
    return <MyTripOrder reservationId={reservationId} />;
  }

  return null;
};

function MyHotelReservation({ reservationId }: { reservationId: string }) {
  const { data } = useSuspenseQuery(reservationQueryOptions(reservationId));

  const reservation = data.hotelReservation;

  return reservation.status === ReservationType.REFUNDED ||
    !reservation.externalReservation ? (
    <OrderDetailsWithoutReservation reservation={reservation} />
  ) : (
    <MyHotel reservation={reservation} />
  );
}

function MyFlightOrder({ reservationId }: { reservationId: string }) {
  const { data } = useSuspenseQuery(reservationQueryOptions(reservationId));

  const reservation = data.flightReservation;

  return !reservation.externalReservation ? (
    <OrderDetailsWithoutReservation reservation={reservation} />
  ) : (
    <MyFlight reservation={reservation} status={reservation.status} />
  );
}

function MyTripOrder({ reservationId }: { reservationId: string }) {
  const { data } = useSuspenseQuery(tripBookingQueryOptions(reservationId));

  const reservation = data.packageBooking;

  return <MyTrip reservation={reservation} />;
}
